

import {clearFrontendConfigCache, lbFetchRaw, preloadFrontendConfig, preloadTenantLanguage, refreshAccessToken, Vue} from "@/extensions";
import {Options} from "vue-class-component";
import {ElCollapseTransition} from "element-plus";
import {LSK_ACCESS_JWT, LSK_REFRESH_JWT} from "@/utils";
import featureUsageTracker from "@/services/feature-usage-tracker";

type TenantOption = {
  id: number
  name: string
}

type TenantSwitchResponse = {
  jwt?: string
  error?: string
}

@Options({
  components: {
    ElCollapseTransition
  }
})
export default class NavBar extends Vue {
  user: any = {}
  currentUser: any = null
  loginMenuVisible = false
  tenantOptions: Array<TenantOption> = []
  selectedTenantId: number | undefined = undefined
  tenantOptionsLoading = false
  tenantSwitchInProgress = false
  tenantSwitchSuppressChange = false

  get shouldShowDocumentCreateDropdown() {
    const activeCreateModules = [
      this.isProcurementModuleActive(),
      this.isInvoiceModuleActive(),
      this.isOutgoingInvoiceModuleActive(),
      this.isBankStatementModuleActive(),
      this.isContractModuleActive() && this.canInsertContract
    ].filter(Boolean).length

    return activeCreateModules > 1
  }

  get shouldShowTenantSwitcher() {
    return !this.isLoggedAsOtherUser() && this.tenantOptions.length > 1
  }

  get canAccessDashboardHub() {
    return this.hasAccessToAnyDashboard || (this.aiEnabled && this.isOperator)
  }

  get isOnlyOutgoingInvoiceModuleActive() {
    return this.isOutgoingInvoiceModuleActive()
        && !this.isInvoiceModuleActive()
        && !this.isProcurementModuleActive()
  }

  mounted() {
    this.emitter.on("reload-navbar", this.updateNavbar)
    this.emitter.on("open-menu", this.open)
    this.emitter.on("close-menu", this.close)
    this.updateNavbar()
  }

  beforeUnmount() {
    this.emitter.off("reload-navbar", this.updateNavbar)
    this.emitter.off("open-menu", this.open)
    this.emitter.off("close-menu", this.close)
  }

  toggle() {
    this.loginMenuVisible = !this.loginMenuVisible
  }

  open() {
    this.loginMenuVisible = true
  }

  close() {
    this.loginMenuVisible = false
  }

  isEmailFromLifbase(email: string) {
    return email.indexOf('@ecm.online') >= 0
  }

  refreshCurrentView() {
    if (this.$route?.name === 'overview') {
      this.emitter.emit("refresh-overview")
      return
    } else {
      this.$router.push({name: 'overview'})
    }
  }

  updateNavbar(args?: any) {
    if (!!args && args.logout) {
      this.currentUser = null
      this.user = {}
      this.resetTenantSwitcher()
    } else {
      this.currentUser = this.getCurrentUser() ?? null
      void Promise.all([
        preloadFrontendConfig().catch(() => []),
        preloadTenantLanguage().catch(() => 'de')
      ]).finally(() => {
        this.$forceUpdate()
      })
      void this.loadTenantOptions()
      this.getUserInfo((user: any) => {
        this.user = user;
        this.$forceUpdate()
      });
    }
    this.$forceUpdate()
  }

  gotoView(
      view: string,
      featureCode: string | null = null,
      eventKey: string | null = null,
      metadata: Record<string, any> = {}
  ) {
    this.loginMenuVisible = false
    if (featureCode && eventKey) {
      featureUsageTracker.trackInteraction(featureCode, eventKey, {
        target_view: view,
        current_route: this.$route?.path ?? null,
        ...metadata
      })
    }
    this.$router.push({name: view})
  }

  resetTenantSwitcher() {
    this.tenantOptions = []
    const tenantId = Number(this.currentUser?.jwtUser?.tenantId)
    this.selectedTenantId = tenantId || undefined
    this.tenantOptionsLoading = false
    this.tenantSwitchInProgress = false
  }

  async loadTenantOptions() {
    if (!this.currentUser?.jwtUser?.userEmail || this.isLoggedAsOtherUser()) {
      this.resetTenantSwitcher()
      return
    }

    this.tenantOptionsLoading = true
    this.tenantSwitchSuppressChange = true
    try {
      const response = await lbFetchRaw({
        url: "/api/auth/tenant-options",
        data: {}
      })

      if (!response.ok) throw new Error("tenant_options_failed")

      const data = await response.json()
      const tenantOptions = Array.isArray(data?.tenants)
          ? data.tenants
              .map((tenant: any) => ({
                id: Number(tenant?.id),
                name: `${tenant?.name || ""}`.trim()
              }))
              .filter((tenant: TenantOption) => tenant.id > 0 && !!tenant.name)
          : []

      this.tenantOptions = tenantOptions
      const currentTenantId = Number(this.currentUser?.jwtUser?.tenantId)
      this.selectedTenantId = tenantOptions.find((tenant: TenantOption) => tenant.id === currentTenantId)?.id
          ?? (currentTenantId || undefined)
    } catch (_e) {
      this.tenantOptions = []
      const tenantId = Number(this.currentUser?.jwtUser?.tenantId)
      this.selectedTenantId = tenantId || undefined
    } finally {
      this.tenantOptionsLoading = false
      this.$nextTick(() => {
        this.tenantSwitchSuppressChange = false
      })
      this.$forceUpdate()
    }
  }

  async onTenantChange(value: number | string) {
    if (this.tenantSwitchSuppressChange) return

    const tenantId = Number(value)
    const currentTenantId = Number(this.currentUser?.jwtUser?.tenantId)
    if (!tenantId || !currentTenantId || tenantId === currentTenantId) {
      this.selectedTenantId = currentTenantId || undefined
      return
    }

    const previousTenantId = currentTenantId
    const previousRefreshJwt = localStorage.getItem(LSK_REFRESH_JWT)
    const previousAccessJwt = localStorage.getItem(LSK_ACCESS_JWT)
    this.tenantSwitchInProgress = true
    this.selectedTenantId = tenantId

    let tokensSwitched = false
    try {
      const response = await lbFetchRaw({
        url: "/api/auth/switch-tenant",
        data: {tenantId}
      })
      const payload = await this.parseTenantSwitchResponse(response)

      if (!response.ok || !payload?.jwt) {
        throw new Error(payload?.error || "tenant_switch_failed")
      }

      localStorage.setItem(LSK_REFRESH_JWT, payload.jwt)
      localStorage.removeItem(LSK_ACCESS_JWT)
      await refreshAccessToken()

      const switchedTenantId = Number(this.getCurrentUser()?.jwtUser?.tenantId)
      if (!localStorage.getItem(LSK_ACCESS_JWT) || switchedTenantId !== tenantId) {
        throw new Error("tenant_switch_failed")
      }
      tokensSwitched = true

      this.persistLastLoginTenant(tenantId)
      clearFrontendConfigCache()
      window.history.replaceState(
          window.history.state,
          "",
          `${window.location.pathname}${window.location.search}#/overview?from=tenant-switch`
      )
      window.location.reload()
    } catch (error: any) {
      if (!tokensSwitched) {
        if (previousRefreshJwt) localStorage.setItem(LSK_REFRESH_JWT, previousRefreshJwt)
        else localStorage.removeItem(LSK_REFRESH_JWT)

        if (previousAccessJwt) localStorage.setItem(LSK_ACCESS_JWT, previousAccessJwt)
        else localStorage.removeItem(LSK_ACCESS_JWT)

        if (!localStorage.getItem(LSK_ACCESS_JWT) && previousRefreshJwt) {
          await refreshAccessToken().catch(() => undefined)
        }
      }

      this.currentUser = this.getCurrentUser() ?? null
      this.selectedTenantId = previousTenantId
      this.showTenantSwitchError(error?.message)
    } finally {
      this.tenantSwitchInProgress = false
    }
  }

  async parseTenantSwitchResponse(response: Response): Promise<TenantSwitchResponse> {
    const text = await response.text()
    if (!text) return {}

    try {
      return JSON.parse(text)
    } catch (_e) {
      return {}
    }
  }

  persistLastLoginTenant(tenantId: number) {
    const email = this.currentUser?.jwtUser?.userEmail?.trim()?.toLowerCase()
    const tenantName = this.tenantOptions.find((tenant: TenantOption) => tenant.id === tenantId)?.name
    if (!email || !tenantName) return

    localStorage.setItem(`liftbase-last-login-tenant:${email}`, tenantName)
  }

  showTenantSwitchError(errorCode?: string) {
    let messageKey = "tenant_switcher.error"
    if (errorCode === "tenant_switch_passkey_not_supported") {
      messageKey = "tenant_switcher.error_passkey"
    } else if (errorCode === "tenant_switch_saml_provider_mismatch") {
      messageKey = "tenant_switcher.error_saml"
    }

    this.$message({
      message: this.$t(messageKey),
      type: "error"
    })
  }

  closeTimeout: any

  startCloseDelay() {
    this.closeMenu()
  }

  cancelCloseDelay() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
  }

  closeMenu() {
    this.closeTimeout = setTimeout(() => {
      this.loginMenuVisible = false;
    }, 1000);
  }

  get isOnboarding() {
    return this.$route.path.indexOf("onboarding") >= 0
  }

}

